/**
 * Shared read-only presentation of a routine's delivery destinations.
 *
 * Used by both the in-chat `ui_routine_summary` card and the routine preview
 * panel, so the two always name and icon the same channel the same way. Note
 * these are the DELIVERY-ROW labels (Strive Inbox included); the external-only
 * labels used by the routines list live in `@/utils/routineOutputChannels`.
 */

import { Inbox } from "lucide-react";
import { AppLogo } from "@/components/AppLogo";
import type { RoutineSummaryChannel } from "@/types/ui-blocks";

export const ROUTINE_CHANNEL_LABELS: Record<RoutineSummaryChannel, string> = {
  strive_inbox: "Strive",
  slack: "Slack",
  google_chat: "Google Chat",
};

export function isRoutineChannel(value: string): value is RoutineSummaryChannel {
  return value in ROUTINE_CHANNEL_LABELS;
}

export interface RoutineDeliveryRow {
  channel: RoutineSummaryChannel;
  detail?: string | null;
}

/**
 * Both surfaces render straight from LLM-streamed tool args, so `delivers_to`
 * can drift from the schema (observed in the wild: rows keyed `provider`
 * instead of `channel`). Accept that alias and drop anything still
 * unrecognisable rather than letting an undefined label crash the card.
 *
 * @param rows - The raw `delivers_to` value as emitted by the model.
 *
 * @returns Only the rows with a channel this UI can render.
 */
export function normalizeDeliveryRows(rows: unknown): RoutineDeliveryRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row): RoutineDeliveryRow[] => {
    if (typeof row !== "object" || row === null) return [];
    const { channel, provider, detail } = row as {
      channel?: unknown;
      provider?: unknown;
      detail?: unknown;
    };
    const key =
      typeof channel === "string" ? channel : typeof provider === "string" ? provider : null;
    if (key === null || !isRoutineChannel(key)) return [];
    return [{ channel: key, detail: typeof detail === "string" ? detail : null }];
  });
}

/** Channel glyph: the in-app inbox has no connector logo, so it uses an icon. */
export function RoutineChannelIcon({ channel }: { channel: RoutineSummaryChannel }) {
  if (channel === "strive_inbox") {
    return <Inbox className="h-4 w-4 text-gray-600" aria-hidden="true" />;
  }
  return (
    <AppLogo shortName={channel} displayName={ROUTINE_CHANNEL_LABELS[channel]} size="sm" />
  );
}
